/**
 * WS-RAIL — the permanent workspace rail.
 *
 * The left chrome does two jobs: it says WHICH WORKSPACE you are in, and it
 * lists the PAGES inside it. Before this, one 272px column did both, with no
 * seam between them — which is the structural reason it read as unorganised
 * even to the person who designed the IA.
 *
 * So the column splits. This 64px rail owns the first job and is ALWAYS
 * present; `WorkspacePanel` owns the second and is what collapsing hides.
 *
 * The bug that forced the split: collapsing used to leave nothing but the
 * workspace button, because `WorkspaceNav` returned `null` when collapsed.
 * Page navigation was not hidden behind icons — it was gone, so the only way
 * to reach another page in your own workspace was to expand the sidebar again.
 * Nielsen Norman's warning about icon-only navigation ("users will be most
 * likely to simply ignore the navigation") understates that case: there was
 * nothing left to ignore.
 *
 * Two decisions taken here rather than escalated, both recorded in
 * `.vibe/_UI_UX` and reversible in one line:
 *
 *   · ADMINISTRATION STAYS IN THE RAIL, below a divider with Platform. It is
 *     still a workspace in the model — moving it to the user cluster would
 *     fork the concept and break `useActiveWorkspace` and the ⌘1–6 shortcuts
 *     for a purely visual gain. A divider says "different kind of thing" at a
 *     fraction of the cost. The band lives on the model (`Workspace.group`),
 *     not as an id list here.
 *
 *   · PLATFORM IS MARKED BY POSITION, NOT BY A SECOND HUE. It used to paint
 *     itself indigo; P3 permits exactly one accent, and a rail makes a second
 *     one louder, not quieter. Below-the-divider placement plus its own glyph
 *     carries "cross-tenant staff tool" without spending a colour the system
 *     has not agreed to.
 */
import type { ReactElement } from "react";
import { Box, Divider, Tooltip } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Link as MuiLink } from "@mui/material";
import { AeriosIQMark, sidebar as sidebarTokens } from "@aeriosiq/ui";
import { HOME_PATH } from "../workspaces/resolve.js";
import { useActiveWorkspace } from "../workspaces/useWorkspaces.js";
import { shortcutHint } from "./workspaceShortcuts.js";
import { NotificationBell } from "../../features/notifications/NotificationBell.js";
import { UserCard } from "./AppSidebar.js";
import { COLLAPSED_WIDTH, RAIL_HEADER_H } from "./primitives.js";
import type { Workspace } from "../workspaces/types.js";

/** The icon button for one workspace. */
function RailButton({
  workspace,
  active,
  onPick,
}: {
  workspace: Workspace;
  active: boolean;
  onPick: (w: Workspace) => void;
}): ReactElement {
  return (
    <Tooltip
      // The tooltip is the label, so it carries what the flyout used to: the
      // task name, the one-line tagline that tells a pilot this is the drone
      // workspace, and the shortcut. An icon rail without this is the pattern
      // the research warns about; with it, the label is one hover away.
      title={`${workspace.label} — ${workspace.tagline} (${shortcutHint(
        workspace.shortcut,
      )})`}
      placement="right"
      disableInteractive
    >
      <Box
        component="button"
        type="button"
        onClick={() => {
          onPick(workspace);
        }}
        aria-label={`${workspace.label} workspace`}
        aria-current={active ? "true" : undefined}
        data-workspace-id={workspace.id}
        data-active={active ? "true" : "false"}
        sx={(t) => {
          const tokens =
            t.palette.mode === "dark"
              ? sidebarTokens.dark
              : sidebarTokens.light;
          return {
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 0.5,
            border: 0,
            cursor: "pointer",
            borderRadius: 1.5,
            bgcolor: active ? tokens.selectedBg : "transparent",
            color: active ? tokens.selectedFg : "text.secondary",
            transition: t.transitions.create(["background-color", "color"], {
              duration: 120,
              easing: "cubic-bezier(0.2, 0, 0, 1)",
            }),
            "&:hover": {
              bgcolor: active ? tokens.selectedBg : tokens.rowHoverBg,
              color: active ? tokens.selectedFg : "text.primary",
            },
            "&:active": {
              bgcolor: active ? tokens.selectedBg : tokens.rowPressedBg,
            },
            "&:focus-visible": {
              outline: `2px solid ${t.palette.primary.main}`,
              outlineOffset: 2,
            },
          };
        }}
      >
        <workspace.Icon fontSize="small" />
      </Box>
    </Tooltip>
  );
}

export function WorkspaceRail(): ReactElement {
  const { visible, workspace, navigateToWorkspace } = useActiveWorkspace();

  const work = visible.filter((w) => (w.group ?? "work") === "work");
  const admin = visible.filter((w) => w.group === "admin");

  return (
    <Box
      sx={(t) => ({
        width: COLLAPSED_WIDTH,
        flexShrink: 0,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        borderRight: `1px solid ${t.palette.divider}`,
        // The rail is the quieter of the two surfaces: the panel is where you
        // read, the rail is where you aim. A half-step down from the canvas
        // draws the seam without a second border (P2).
        bgcolor:
          t.palette.mode === "dark"
            ? t.palette.background.default
            : t.palette.background.paper,
      })}
    >
      {/* The mark is the whole brand here — per D86 the mark alone is always a
          valid rendering, because the mark IS the IQ. The lockup never appears
          in the rail, which also settles the "logo looks displaced" complaint:
          there is no longer a wordmark to align against a nav column. */}
      <Box
        sx={{
          height: RAIL_HEADER_H,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <MuiLink
          component={RouterLink}
          to={HOME_PATH}
          aria-label="AeriosIQ — go to Home"
          underline="none"
          sx={{
            display: "inline-flex",
            color: "inherit",
            borderRadius: 1,
            "&:focus-visible": {
              outline: (t) => `2px solid ${t.palette.primary.main}`,
              outlineOffset: 3,
            },
          }}
        >
          <AeriosIQMark height={22} />
        </MuiLink>
      </Box>

      <Box
        component="ul"
        aria-label="Workspaces"
        sx={{
          m: 0,
          p: 0,
          pt: 0.5,
          listStyle: "none",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {work.map((w) => (
          <Box component="li" key={w.id} sx={{ listStyle: "none" }}>
            <RailButton
              workspace={w}
              active={workspace?.id === w.id}
              onPick={navigateToWorkspace}
            />
          </Box>
        ))}
        {admin.length > 0 ? (
          <Box component="li" sx={{ listStyle: "none" }}>
            <Divider sx={{ mx: 1.5, my: 1 }} />
          </Box>
        ) : null}
        {admin.map((w) => (
          <Box component="li" key={w.id} sx={{ listStyle: "none" }}>
            <RailButton
              workspace={w}
              active={workspace?.id === w.id}
              onPick={navigateToWorkspace}
            />
          </Box>
        ))}
      </Box>

      {/* The utility cluster. The bell used to sit in its own row with
          `justifyContent: flex-end` — the only right-aligned element in a
          left-aligned list, which is why it read as floating between the two
          things below it. Here it is simply the item above the avatar. */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pb: 0.5,
        }}
      >
        <NotificationBell />
      </Box>
      <UserCard collapsed />
    </Box>
  );
}
